import { z } from 'zod';
import type {
  ConsentListener,
  ConsentPreferences,
  CookiePro,
  CookieProConfiguration,
  CookieProFailure,
} from '../models/services/consent';
import { log } from '../utils/log';

/**
 * CookiePro is a small state machine:
 * - configure() stores the tenant-specific script settings
 * - initialise() loads or reuses the provider script and resolves the first consent snapshot
 * - OneTrustGroupsUpdated updates the live snapshot after init
 *
 * `scriptId` is only the DOM id for the script tag. `brandScriptId` is the CookiePro tenant id.
 */
const GROUPS_UPDATED = 'OneTrustGroupsUpdated';
const EMPTY_GROUPS: readonly string[] = Object.freeze([]);
const DEFAULT_CATEGORIES = Object.freeze({
  necessary: 'C0001',
  performance: 'C0002',
  marketing: 'C0004',
});
const groupIdSchema = z
  .string()
  .trim()
  .regex(/^[A-Za-z0-9_-]+$/);
const configurationSchema = z
  .object({
    brandScriptId: z.string().trim().min(1),
    src: z.url(),
    scriptId: z
      .string()
      .trim()
      .regex(/^[A-Za-z][A-Za-z0-9_-]*$/)
      .default('cookiePro'),
  })
  .strict();

type NormalizedConfiguration = CookieProConfiguration &
  Readonly<Required<Pick<CookieProConfiguration, 'scriptId'>>>;

// Local browser types avoid conflicting with existing tenant Window augmentations.
type CookieProWindow = Window & {
  OneTrust?: { ToggleInfoDisplay?: () => void };
  OnetrustActiveGroups?: string;
};

const unresolvedSnapshot = (status: 'unconfigured' | 'loading'): ConsentPreferences =>
  Object.freeze({
    status,
    source: 'unresolved',
    necessary: true,
    performance: false,
    marketing: false,
    activeGroups: EMPTY_GROUPS,
  });

let configuration: NormalizedConfiguration | undefined;
let snapshot = unresolvedSnapshot('unconfigured');
let initialisation: Promise<ConsentPreferences> | undefined;
let providerScript: HTMLScriptElement | undefined;
const listeners = new Set<ConsentListener>();

const summarizePreferences = (preferences: ConsentPreferences) => ({
  status: preferences.status,
  source: preferences.source,
  necessary: preferences.necessary,
  performance: preferences.performance,
  marketing: preferences.marketing,
  activeGroups: preferences.activeGroups,
});

const isPreferenceUpdate = (previous: ConsentPreferences, next: ConsentPreferences): boolean =>
  previous.status === 'ready' && next.status === 'ready';

const publish = (next: ConsentPreferences): void => {
  if (JSON.stringify(snapshot) === JSON.stringify(next)) return;

  const consentRevoked =
    isPreferenceUpdate(snapshot, next) &&
    snapshot.activeGroups.some((group) => !next.activeGroups.includes(group));

  if (isPreferenceUpdate(snapshot, next)) {
    // Log only post-init preference changes; init logs live in finish().
    log.debug({
      message: 'CookiePro preferences changed',
      data: {
        from: summarizePreferences(snapshot),
        to: summarizePreferences(next),
      },
    });
  }

  snapshot = next;
  for (const listener of [...listeners]) {
    try {
      listener(next);
    } catch (error) {
      // One faulty observer must not prevent other consumers receiving a denial.
      console.error('CookiePro consent subscriber failed.', error);
    }
  }
  if (consentRevoked && typeof window !== 'undefined') {
    window.location.reload();
  }
};

const normalizeConfiguration = (input: CookieProConfiguration): NormalizedConfiguration => {
  const result = configurationSchema.safeParse(input);
  if (!result.success) {
    throw new TypeError(`Invalid CookiePro configuration: ${result.error.message}`);
  }

  const parsed = result.data;
  return Object.freeze({
    ...parsed,
    src: new URL(parsed.src).href,
  });
};

const configure = (input: CookieProConfiguration): void => {
  const next = normalizeConfiguration(input);
  if (configuration) {
    if (JSON.stringify(configuration) !== JSON.stringify(next)) {
      throw new Error('CookiePro is already configured for a different tenant or policy.');
    }
    return;
  }

  configuration = next;
};

const normalizeGroups = (value: unknown): readonly string[] | undefined => {
  if (!Array.isArray(value) || !value.every((group) => typeof group === 'string')) {
    return undefined;
  }

  const groups = value.map((group: string) => group.trim()).filter(Boolean);
  if (groups.some((group) => !groupIdSchema.safeParse(group).success)) return undefined;
  return Object.freeze([...new Set(groups)].sort());
};

const initialise = (): Promise<ConsentPreferences> => {
  if (!configuration) throw new Error('Configure CookiePro before initializing it.');
  if (initialisation) return initialisation;

  const config = configuration;
  let resolveInitialisation!: (preferences: ConsentPreferences) => void;
  initialisation = new Promise<ConsentPreferences>((resolve) => {
    resolveInitialisation = resolve;
  });

  let settled = false;
  let timeout: number | undefined;
  let script: HTMLScriptElement | undefined;
  const finish = (next: ConsentPreferences): void => {
    if (settled) return;

    // Emit the resolved startup snapshot once when initialisation settles.
    log.debug({
      message: 'CookiePro initial preferences',
      data: summarizePreferences(next),
    });

    settled = true;
    if (timeout !== undefined) window.clearTimeout(timeout);
    script?.removeEventListener('load', handleLoad);
    script?.removeEventListener('error', handleError);
    resolveInitialisation(next);
  };
  const fail = (code: CookieProFailure['code'], message: string): void => {
    const next: ConsentPreferences = Object.freeze({
      status: 'error',
      source: 'fallback',
      necessary: true,
      performance: false,
      marketing: false,
      activeGroups: EMPTY_GROUPS,
      error: Object.freeze({ code, message }),
    });
    finish(next);
    publish(next);
  };
  const applyGroups = (groups: readonly string[]): void => {
    const next: ConsentPreferences = Object.freeze({
      status: 'ready',
      source: 'provider',
      necessary: true,
      performance: groups.includes(DEFAULT_CATEGORIES.performance),
      marketing: groups.includes(DEFAULT_CATEGORIES.marketing),
      activeGroups: groups,
    });
    finish(next);
    publish(next);
  };
  const handleGroups = (event: Event): void => {
    const groups = normalizeGroups((event as CustomEvent<unknown>).detail);
    if (!groups) {
      fail('invalid-groups', 'OneTrust supplied an invalid active-group list.');
      return;
    }
    applyGroups(groups);
  };
  const hydrateProvider = (): void => {
    const provider = window as CookieProWindow;
    if (!provider.OneTrust || typeof provider.OnetrustActiveGroups !== 'string') return;
    const groups = normalizeGroups(provider.OnetrustActiveGroups.split(','));
    // A populated Always Active group distinguishes initialized state from a stub.
    if (groups?.includes(DEFAULT_CATEGORIES.necessary)) applyGroups(groups);
  };
  function handleLoad(): void {
    // The stub download is not readiness; it may still be fetching the actual CMP.
    hydrateProvider();
  }
  function handleError(): void {
    fail('script-error', 'The CookiePro script could not be loaded.');
  }

  if (typeof window === 'undefined' || typeof document === 'undefined') {
    fail('browser-unavailable', 'CookiePro requires a browser document.');
    return initialisation;
  }

  publish(unresolvedSnapshot('loading'));
  try {
    const identifiedElement = document.getElementById(config.scriptId);
    const candidates = new Set(document.querySelectorAll('script[data-domain-script]'));
    if (identifiedElement) candidates.add(identifiedElement);

    const [candidate] = candidates;
    if (
      candidates.size > 1 ||
      (candidate &&
        (!(candidate instanceof HTMLScriptElement) ||
          candidate.getAttribute('data-domain-script') !== config.brandScriptId ||
          candidate.src !== config.src))
    ) {
      fail('script-conflict', 'Another element or CookiePro configuration is already present.');
      return initialisation;
    }

    // Never assume an unidentified preloaded provider belongs to this tenant.
    if (!candidate && (window as CookieProWindow).OneTrust) {
      fail('script-conflict', 'An existing OneTrust provider has no matching script.');
      return initialisation;
    }

    script = candidate as HTMLScriptElement | undefined;
    const existingScript = Boolean(script);
    if (!script) {
      script = document.createElement('script');
      script.id = config.scriptId;
      script.src = config.src;
      script.async = true;
      script.setAttribute('data-domain-script', config.brandScriptId);
      script.setAttribute('data-document-language', 'true');
    }

    providerScript = script;
    window.addEventListener(GROUPS_UPDATED, handleGroups);
    script.addEventListener('load', handleLoad);
    script.addEventListener('error', handleError);
    timeout = window.setTimeout(
      () => fail('timeout', 'OneTrust did not provide preferences before the readiness timeout.'),
      3000,
    );

    if (existingScript) hydrateProvider();
    else document.head.insertBefore(script, document.head.firstChild);
  } catch (_error) {
    window.removeEventListener(GROUPS_UPDATED, handleGroups);
    providerScript = undefined;
    fail('script-error', 'The CookiePro script could not be initialized.');
  }

  // Keep the group listener after settlement, including timeouts, for late updates.
  return initialisation;
};

const subscribe = (listener: ConsentListener): (() => void) => {
  if (typeof listener !== 'function') {
    throw new TypeError('A CookiePro subscriber must be a function.');
  }
  // Separate subscriptions stay independent even when given the same callback.
  const observer: ConsentListener = (preferences) => listener(preferences);
  listeners.add(observer);
  return () => {
    listeners.delete(observer);
  };
};

const openPreferences = (): boolean => {
  if (!providerScript || typeof window === 'undefined') return false;
  const provider = (window as CookieProWindow).OneTrust;
  if (typeof provider?.ToggleInfoDisplay !== 'function') return false;

  try {
    provider.ToggleInfoDisplay();
    return true;
  } catch (_error) {
    return false;
  }
};

/**
 * One instance per app/document. Importing it has no browser or network side effects.
 * OneTrust owns saved choices, privacy signals and default policy; this service only
 * observes its effective state and reloads when a previously active category is revoked.
 * It never accepts consent or initializes trackers.
 */
export const cookiePro: CookiePro = Object.freeze({
  configure,
  initialise,
  getSnapshot: () => snapshot,
  subscribe,
  openPreferences,
});
